import Accordion from '@/common/components/Accordion';
import HighlightTextArea from '@/common/components/HighlightTextArea';
import SelectController from '@/common/components/SelectController';
import { AvailableTokensAccordion } from '@/common/components/AvailableTokensAccordion'
import { fiscalDocumentKindOptions } from '@/features/privateCompanies/constants/fiscalDocumentKind.constant';
import { PrivateCompanyLeadsFunnelUpdateValidationSchema } from '@/features/privateCompanyLeadsFunnel/schemas/update.schema';
import { FiscalDocumentInformationData } from '@/features/publicCompanyLeadsFunnel/types/publicCompanyLead';
import { BaseSyntheticEvent } from 'react';
import { Control, Controller, FieldErrors } from 'react-hook-form';
import { BillingBillsDescriptionTokensTransformer } from '@/features/privateCompanies/services/billingBillsDescriptionTokensTransformer';
import { useBillingBillsDescriptionTokens } from '@/common/hooks/queries/useBillingBillsDescriptionTokens';
import { Mapper } from '@/common/services/mapper';

interface FiscalDocumentInformationSectionProps {
  fiscal_document_information?: FiscalDocumentInformationData;
  control: Control<PrivateCompanyLeadsFunnelUpdateValidationSchema, any>;
  errors: FieldErrors<PrivateCompanyLeadsFunnelUpdateValidationSchema>;
  submittableOnBlur?: (
    e: React.FocusEvent<HTMLInputElement, Element>,
    onBlur: (e: React.FocusEvent<HTMLInputElement, Element>) => void,
  ) => void;
  submit?: (e?: BaseSyntheticEvent) => Promise<void>;
  disabled?: boolean;
}

export const FiscalDocumentInformationSection = ({
  fiscal_document_information,
  control,
  errors,
  submittableOnBlur,
  submit,
  disabled,
}: FiscalDocumentInformationSectionProps) => {
  const { data: descriptionTokens, dataUpdatedAt } = useBillingBillsDescriptionTokens({
    mapper: Mapper.mapWithTransformer({ transformer: BillingBillsDescriptionTokensTransformer.suggestionsTransformer() })
  });

  return (
    <Accordion
      className="py-1"
      defaultOpen
      head={<p>Detalhes Fiscais <span className="text-danger-soft"> *</span></p>}
      body={
        <div className="flex flex-col gap-4">

          <SelectController
            control={control}
            name="fiscal_document_information.fiscal_document_kind"
            label="Tipo de nota fiscal de consumo"
            triggerOnChange={submit}
            isDisabled={disabled}
            options={fiscalDocumentKindOptions}
            defaultValue={fiscalDocumentKindOptions.find(({ value }) => value === fiscal_document_information?.fiscal_document_kind)}
            errorMessage={errors?.fiscal_document_information?.fiscal_document_kind?.message}
          />

          <Controller
            control={control}
            name="fiscal_document_information.consumption_observations"
            defaultValue={fiscal_document_information?.consumption_observations}
            disabled={disabled}
            render={({ field: { onBlur, value, ...rest } }) => (
              <HighlightTextArea
                label="NF de Consumo"
                rows={16}
                maxLength={1024}
                {...rest}
                value={value as string | undefined}
                errorMessage={errors?.fiscal_document_information?.consumption_observations?.message}
                onBlur={(e) => submittableOnBlur?.(e as unknown as React.FocusEvent<HTMLInputElement, Element>, onBlur)}
                disabled={disabled}
                suggestions={descriptionTokens}
              />
            )}
          />

          <Controller
            control={control}
            name="fiscal_document_information.service_observations"
            defaultValue={fiscal_document_information?.service_observations}
            disabled={disabled}
            render={({ field: { onBlur, value, ...rest } }) => (
              <HighlightTextArea
                label="NF de Tarifas"
                rows={16}
                maxLength={1024}
                {...rest}
                value={value as string | undefined}
                errorMessage={errors?.fiscal_document_information?.service_observations?.message}
                onBlur={(e) => submittableOnBlur?.(e as unknown as React.FocusEvent<HTMLInputElement, Element>, onBlur)}
                disabled={disabled}
                suggestions={descriptionTokens}
              />
            )}
          />

          <AvailableTokensAccordion descriptionTokens={descriptionTokens} updatedAt={dataUpdatedAt} />
        </div>
      }
    />
  );
};
